import { Room, Player, Round, Submission } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import RoomHeader from "@/components/RoomHeader";

interface Props {
  room: Room;
  players: Player[];
  round: Round;
  submissions: Submission[];
  me: Player;
  amHost: boolean;
}

function numericValue(v: string): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function PokerReveal({ room, players, round, submissions, me, amHost }: Props) {
  const numeric = submissions.map((s) => ({ s, n: numericValue(s.value) })).filter((x) => x.n !== null) as {
    s: Submission;
    n: number;
  }[];
  const nums = numeric.map((x) => x.n);
  const avg = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
  const min = nums.length ? Math.min(...nums) : 0;
  const max = nums.length ? Math.max(...nums) : 0;
  const spread = max - min;

  // Alignment: 100% if all same; lower as spread grows relative to max
  const alignment = nums.length <= 1
    ? 100
    : Math.max(0, Math.round(100 - (spread / Math.max(max, 1)) * 100));

  const alignColor =
    alignment >= 75 ? "text-success" : alignment >= 40 ? "text-warning" : "text-destructive";
  const meterColor =
    alignment >= 75 ? "bg-success" : alignment >= 40 ? "bg-warning" : "bg-destructive";

  const grouped: Record<string, Player[]> = {};
  for (const s of submissions) {
    const p = players.find((pl) => pl.id === s.player_id);
    if (!p) continue;
    grouped[s.value] = grouped[s.value] ?? [];
    grouped[s.value].push(p);
  }

  const markRefined = async () => {
    await supabase.from("rounds").update({ refined: true, status: "finished" }).eq("id", round.id);
  };

  const nextStory = async () => {
    await markRefined();
    const { data: rd } = await supabase
      .from("rounds")
      .insert({
        room_id: room.id,
        round_number: round.round_number + 1,
        prompt: `Story #${round.round_number + 1}`,
        status: "collecting",
      })
      .select()
      .single();
    await supabase
      .from("rooms")
      .update({ status: "playing", current_round_id: rd!.id })
      .eq("id", room.id);
  };

  const endSession = async () => {
    await markRefined();
    await supabase.from("rooms").update({ status: "finished" }).eq("id", room.id);
  };

  const discardStory = async () => {
    // Skip without counting as refined
    await supabase.from("rounds").update({ status: "finished" }).eq("id", round.id);
    const { data: rd } = await supabase
      .from("rounds")
      .insert({
        room_id: room.id,
        round_number: round.round_number + 1,
        prompt: `Story #${round.round_number + 1}`,
        status: "collecting",
      })
      .select()
      .single();
    await supabase
      .from("rooms")
      .update({ status: "playing", current_round_id: rd!.id })
      .eq("id", room.id);
  };

  return (
    <main className="min-h-screen px-4 pb-6">
      <RoomHeader room={room} me={me} amHost={amHost} players={players} />
      <div className="max-w-2xl mx-auto space-y-5 animate-pop-in pt-2">
        <div className="text-center text-xs font-semibold text-muted-foreground">
          STORY #{round.round_number} · REVEALED
        </div>
        <Card className="p-5 border-2 shadow-card text-center">
          <h2 className="text-xl font-extrabold">{round.prompt}</h2>
        </Card>

        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 text-center border-2 shadow-card">
            <div className="text-xs font-semibold text-muted-foreground">AVG</div>
            <div className="text-3xl font-extrabold text-primary">
              {nums.length ? avg.toFixed(1) : "—"}
            </div>
          </Card>
          <Card className="p-4 text-center border-2 shadow-card">
            <div className="text-xs font-semibold text-muted-foreground">MIN</div>
            <div className="text-3xl font-extrabold">{nums.length ? min : "—"}</div>
          </Card>
          <Card className="p-4 text-center border-2 shadow-card">
            <div className="text-xs font-semibold text-muted-foreground">MAX</div>
            <div className="text-3xl font-extrabold">{nums.length ? max : "—"}</div>
          </Card>
        </div>

        <Card className="p-5 border-2 shadow-pop">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Alignment Meter
            </div>
            <div className={`text-2xl font-extrabold ${alignColor}`}>{alignment}%</div>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full ${meterColor} transition-all duration-1000`}
              style={{ width: `${alignment}%` }}
            />
          </div>
        </Card>

        <Card className="p-5 border-2 shadow-card">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
            All votes
          </div>
          <div className="space-y-2">
            {Object.entries(grouped)
              .sort((a, b) => b[1].length - a[1].length)
              .map(([val, ps]) => {
                const n = numericValue(val);
                const isOutlier = n !== null && nums.length > 2 && (n === min || n === max) && spread >= 5;
                return (
                  <div
                    key={val}
                    className={`flex items-center gap-3 rounded-xl border-2 p-3 ${
                      isOutlier ? "border-warning bg-warning/10" : "border-border"
                    }`}
                  >
                    <div
                      className={`h-12 w-10 rounded-lg flex items-center justify-center text-xl font-extrabold ${
                        isOutlier ? "bg-warning text-warning-foreground" : "gradient-primary text-white"
                      }`}
                    >
                      {val}
                    </div>
                    <div className="flex-1 text-sm font-semibold">
                      {ps.map((p) => p.name).join(", ")}
                    </div>
                    {isOutlier && (
                      <span className="text-xs font-bold text-warning-foreground bg-warning rounded-full px-2 py-0.5">
                        outlier
                      </span>
                    )}
                  </div>
                );
              })}
            {submissions.length === 0 && (
              <div className="text-center text-muted-foreground text-sm py-4">No votes</div>
            )}
          </div>
        </Card>

        {amHost ? (
          <div className="space-y-2">
            <Button onClick={nextStory} size="lg" className="w-full h-12 gradient-primary font-bold">
              Mark Refined & Next Story →
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={discardStory} variant="outline">
                Skip (don't count)
              </Button>
              <Button onClick={endSession} variant="outline">
                End session
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground animate-pulse">Waiting for host…</div>
        )}
      </div>
    </main>
  );
}
